/**
 * useCvFileAnalysis
 *
 * Owns everything related to ATS scoring of the user's CV:
 *
 * - **Real-time preview** — a debounced `analyzeCV()` pass over the currently
 *   edited CV, producing a small score card displayed in the ATS sidebar.
 * - **Full file upload analysis** — parses a user-uploaded PDF (via pdfjs-dist)
 *   or DOCX (via mammoth), runs `analyzeFile()` on the extracted text, and
 *   exposes a rich `ATSCompleteAnalysisResult` to the UI.
 * - **AI parse** — sends the last extracted text to `/api/ai?route=parse`
 *   and, after user confirmation, overwrites the current CV with the
 *   AI-extracted fields.
 *
 * Imports of `mammoth` and `pdfjs-dist` are dynamic so they stay out of the
 * initial bundle (these libs are only needed when the user clicks upload).
 */

import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import type { CVData } from '../types';
import {
  analyzeCV,
  analyzeFile,
  type ATSPreviewResult,
  type ATSCompleteAnalysisResult,
} from '../lib/ats-analyzer';
import { extractCvFile } from '../lib/cv-file-extract';
import { parseJsonResponse } from '../lib/fetch-utils';
import { toast } from '../lib/toast';

export interface UseCvFileAnalysisParams {
  cvData: CVData;
  effectiveJobDescription: string;
  /** Setter from useCvState — used by handleAiParseClick to overwrite the
   *  active CV when the user confirms the replacement. */
  setData: Dispatch<SetStateAction<CVData>>;
}

export interface UseCvFileAnalysisReturn {
  // Real-time preview
  previewResult: ATSPreviewResult | null;

  // Full-file analysis
  completeAnalysisResult: ATSCompleteAnalysisResult | null;
  isAnalyzingFile: boolean;
  showCompleteAnalysis: boolean;
  setShowCompleteAnalysis: Dispatch<SetStateAction<boolean>>;
  lastExtractedText: string;

  // AI parse-to-CV
  isAiParsing: boolean;

  // Actions
  handleFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  handleAiParseClick: () => Promise<void>;
}

function buildFailedAnalysis(
  fileType: 'pdf' | 'docx',
): ATSCompleteAnalysisResult {
  return {
    score: 0,
    precision: 0,
    recall: 0,
    formatScore: 0,
    keywordScore: 0,
    experienceScore: 0,
    contentScore: 0,
    completenessScore: 0,
    fieldMetrics: {
      contact: { precision: 0, recall: 0, f1: 0, status: 'critical' },
      skills: { precision: 0, recall: 0, f1: 0, status: 'critical' },
      experience: { precision: 0, recall: 0, f1: 0, status: 'critical' },
      education: { precision: 0, recall: 0, f1: 0, status: 'critical' },
      dates: { precision: 0, recall: 0, f1: 0, status: 'critical' },
      sections: { precision: 0, recall: 0, f1: 0, status: 'critical' },
    },
    errorAnalysis: {
      extractionMiss: ['Parsing complet échoué'],
      extractionFalse: [],
      formatError: [fileType === 'pdf' ? 'PDF non lisible' : 'DOCX non lisible'],
      partialMatch: [],
    },
    industryComparison: [
      { tool: 'CVShelf', f1: 98, delta: -98, status: 'below' },
      { tool: 'Industry Average', f1: 90, delta: -90, status: 'below' },
    ],
    validationStatus: 'not_ready',
    validationMessage: "❌ NOT READY - Fichier non lisible par l'ATS",
    redFlags: ['Parsing complet échoué', 'F1-Score = 0%'],
    yellowFlags: [],
    greenLights: [],
    feedback: [
      {
        type: 'error',
        message: "Erreur de parsing - fichier non lisible par l'ATS.",
        category: 'format',
      },
    ],
    extractedText: '',
    detectedSections: [],
    warnings: [
      fileType === 'pdf'
        ? "Le PDF n'a pas pu être parsé (possible PDF image/scanné)."
        : "Le DOCX n'a pas pu être lu.",
      'Les ATS réels auraient le même problème avec ce fichier.',
    ],
    recommendations: [
      'Utilisez un format .docx (meilleur parsing ATS).',
      'Pour PDF: assurez-vous que le texte est sélectionnable.',
      'Évitez les PDFs scannés ou basés sur des images.',
    ],
    fileType,
    parsingSuccess: false,
    keywordCoverage: 0,
    actionVerbCount: 0,
    quantifiedResults: 0,
    experienceYears: 0,
  };
}

export function useCvFileAnalysis(
  params: UseCvFileAnalysisParams,
): UseCvFileAnalysisReturn {
  const { cvData, effectiveJobDescription, setData } = params;

  const [previewResult, setPreviewResult] = useState<ATSPreviewResult | null>(null);
  const [completeAnalysisResult, setCompleteAnalysisResult] =
    useState<ATSCompleteAnalysisResult | null>(null);
  const [isAnalyzingFile, setIsAnalyzingFile] = useState(false);
  const [showCompleteAnalysis, setShowCompleteAnalysis] = useState(false);
  const [lastExtractedText, setLastExtractedText] = useState<string>('');
  const [isAiParsing, setIsAiParsing] = useState(false);

  const analyzeDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Real-time preview analysis (debounced 300 ms).
  useEffect(() => {
    if (analyzeDebounceRef.current) clearTimeout(analyzeDebounceRef.current);
    analyzeDebounceRef.current = setTimeout(() => {
      const result = analyzeCV(cvData, effectiveJobDescription);
      setPreviewResult(result);
    }, 300);
    return () => {
      if (analyzeDebounceRef.current) clearTimeout(analyzeDebounceRef.current);
    };
  }, [cvData, effectiveJobDescription]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const fileName = file.name.toLowerCase();
    const isPdf = file.type === 'application/pdf' || fileName.endsWith('.pdf');
    const isDocx =
      file.type ===
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      fileName.endsWith('.docx');

    if (!isPdf && !isDocx) {
      toast.error('Veuillez charger un fichier PDF ou DOCX.');
      return;
    }

    setIsAnalyzingFile(true);
    setShowCompleteAnalysis(true);

    const fileType: 'pdf' | 'docx' = isPdf ? 'pdf' : 'docx';

    try {
      // Uses the shared util — loads pdfjs/mammoth from jsdelivr CDN at runtime
      // (bundler marks these libs as external).
      const { text: extractedText } = await extractCvFile(file);
      setLastExtractedText(extractedText);
      const result = analyzeFile(extractedText, fileType, effectiveJobDescription);
      setCompleteAnalysisResult(result);
    } catch (error) {
      console.error('Parsing error:', error);
      setCompleteAnalysisResult(buildFailedAnalysis(fileType));
    } finally {
      setIsAnalyzingFile(false);
      e.target.value = '';
    }
  };

  const handleAiParseClick = async () => {
    if (!lastExtractedText.trim()) {
      toast.error(
        'Aucun texte extrait. Chargez d\'abord un PDF ou DOCX dans "Analyse complète ATS".',
      );
      return;
    }
    setIsAiParsing(true);
    try {
      const res = await fetch('/api/ai', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ route: 'parse', text: lastExtractedText }),
      });
      const json = await parseJsonResponse<{ error?: string; data?: unknown }>(res);
      if (!res.ok) throw new Error(json.error || 'Erreur parsing IA');
      const parsed = json.data;
      if (parsed && typeof parsed === 'object') {
        if (
          window.confirm(
            "Remplacer le CV actuel par les champs extraits par l'IA ?",
          )
        ) {
          setData(parsed as CVData);
        }
      }
    } catch (e) {
      console.error('[Parsing IA] Error:', e);
      const msg = e instanceof Error ? e.message : 'Erreur parsing IA';
      const hintMissingKey =
        (msg.includes('OPENROUTER_API_KEY') ||
          msg.includes('MINIMAX_API_KEY') ||
          msg.includes('Aucune IA')) &&
        (msg.includes('not set') ||
          msg.includes('non configurée') ||
          msg.includes('Définissez'))
          ? ' En production : Settings → Environment Variables. En local : .env (OPENROUTER_API_KEY ou MINIMAX_API_KEY).'
          : '';
      toast.error(msg + hintMissingKey);
    } finally {
      setIsAiParsing(false);
    }
  };

  return {
    previewResult,
    completeAnalysisResult,
    isAnalyzingFile,
    showCompleteAnalysis,
    setShowCompleteAnalysis,
    lastExtractedText,
    isAiParsing,
    handleFileUpload,
    handleAiParseClick,
  };
}
